import { create } from 'zustand';
import type { Meld, MeldPos, Tile } from '@rummikub/engine';

export interface RemoteState {
  roomId: string;
  hostId: string;
  status: 'WAITING' | 'PLAYING' | 'FINISHED';
  settings: { turnTimeLimit: number; allowJokerInInitialMeld: boolean };
  players: Array<{
    id: string;
    name: string;
    avatarUrl: string;
    rack: Tile[];
    rackCount: number;
    status: 'ONLINE' | 'OFFLINE';
    hasMelted: boolean;
  }>;
  board: Meld[];
  boardLayout: MeldPos[];
  turnInitialBoard: Meld[];
  poolCount: number;
  currentTurnPlayerId: string;
  turnVersion: number;
  turnStartTime: number;
  consecutivePassCount: number;
  lastRoundCounter: number | null;
}

interface Store {
  userId: string;
  userName: string;
  connected: boolean;
  remote: RemoteState | null;
  errorMsg: string | null;
  gameOver: { trigger: string; results: Array<{ userId: string; score: number; isWinner: boolean; residual: number }> } | null;
  draftBoard: Meld[];
  draftLayout: MeldPos[];
  draftRack: Tile[];
  /** 本回合起始時的手牌（tile id 集合），用於阻擋場上的牌被拿回手牌 */
  turnInitialRackIds: Set<string>;
  /** 等待 server 強制覆蓋本地 draft（按下 undo / reset 時設 true） */
  awaitingForceSync: boolean;

  setConnected: (v: boolean) => void;
  setRemote: (s: RemoteState) => void;
  setError: (msg: string | null) => void;
  setGameOver: (o: Store['gameOver']) => void;
  setDraft: (board: Meld[], rack: Tile[], layout: MeldPos[]) => void;
  resetDraft: () => void;
  markForceSync: () => void;
}

// 每分頁獨立身份：sessionStorage 僅在此分頁存活。
// 這樣同一瀏覽器開兩個分頁測試時，兩分頁是不同玩家，不會互踢。
// 想跨分頁保持同一身份（真實使用情境），可改為 localStorage。
const savedId = sessionStorage.getItem('userId') ?? crypto.randomUUID();
const savedName = sessionStorage.getItem('userName') ?? localStorage.getItem('userName') ?? '';
sessionStorage.setItem('userId', savedId);

export const useStore = create<Store>((set, get) => ({
  userId: savedId,
  userName: savedName,
  connected: false,
  remote: null,
  errorMsg: null,
  gameOver: null,
  draftBoard: [],
  draftLayout: [],
  draftRack: [],
  turnInitialRackIds: new Set(),
  awaitingForceSync: false,

  setConnected: (v) => set({ connected: v }),
  setError: (errorMsg) => set({ errorMsg }),
  setGameOver: (gameOver) => set({ gameOver }),
  setDraft: (draftBoard, draftRack, draftLayout) => set({ draftBoard, draftRack, draftLayout }),
  markForceSync: () => set({ awaitingForceSync: true }),
  setRemote: (next) => {
    const prev = get().remote;
    const myId = get().userId;
    const me = next.players.find((p) => p.id === myId);
    const isMyTurn = next.currentTurnPlayerId === myId && next.status === 'PLAYING';

    // 安全的「全量重置 draft」時機（不會洗掉玩家排版）：
    // 1. 第一次 setRemote
    // 2. turnVersion 變了（換回合 / 撤銷 / 重設成功 → 新 snapshot）
    // 3. 換手了（不是我的回合）
    // 4. 狀態改變（WAITING→PLAYING / →FINISHED）
    const turnChanged = !prev
      || prev.turnVersion !== next.turnVersion
      || prev.currentTurnPlayerId !== next.currentTurnPlayerId
      || prev.status !== next.status;

    if (turnChanged || !isMyTurn) {
      // 換回合：rebase 本回合的手牌起點（用於擋「場上牌拿回手牌」）
      set({
        remote: next,
        draftBoard: next.board.map((m) => m.slice()),
        draftLayout: (next.boardLayout ?? []).slice(),
        draftRack: me?.rack.slice() ?? [],
        turnInitialRackIds: isMyTurn ? new Set((me?.rack ?? []).map((t) => t.id)) : get().turnInitialRackIds,
        awaitingForceSync: false,
      });
      return;
    }

    // 強制同步（玩家剛按下 undo / reset / draw）
    if (get().awaitingForceSync) {
      set({
        remote: next,
        draftBoard: next.board.map((m) => m.slice()),
        draftLayout: (next.boardLayout ?? []).slice(),
        draftRack: me?.rack.slice() ?? [],
        awaitingForceSync: false,
      });
      return;
    }

    // 我的回合中、turnVersion 沒變 → 多半是別人加入 / 離開房間的廣播。
    // 為避免覆蓋玩家排到一半的版面，僅更新「非 draft」欄位：保留 draftBoard / draftLayout / draftRack。
    const draft = get().draftBoard;
    const echo = JSON.stringify(draft) === JSON.stringify(next.board);
    if (echo) {
      // 是我自己的 moveTile 回聲 → 同步 layout（server 可能 normalize）
      set({ remote: next, draftLayout: (next.boardLayout ?? get().draftLayout).slice() });
    } else {
      // 保留本地排版
      set({ remote: next });
    }
  },
  resetDraft: () => {
    const { remote, userId } = get();
    if (!remote) return;
    const me = remote.players.find((p) => p.id === userId);
    set({
      draftBoard: remote.board.map((m) => m.slice()),
      draftLayout: (remote.boardLayout ?? []).slice(),
      draftRack: me?.rack.slice() ?? [],
    });
  },
}));
